import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Steps,
  Divider,
  message,
  Typography,
  Row,
  Col,
  Tooltip,
} from 'antd';
import { SaveOutlined, ArrowRightOutlined, MailOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import apiService, { Lead, User } from '../../services/api';
import { useCognitoAuth } from '../../contexts/CognitoAuthContext';
import { userMayUseClientManagementEmailButton } from '../../utils/clientManagementEmailAccess';
import EmailModal from '../EmailModal';
import {
  TELECOM_SECTIONS,
  ENERGY_SECTIONS,
} from '../statusConstants';
import {
  workspaceSurface,
  workspaceCardHead,
  workspaceCardBody,
  workspaceInset,
} from './workspaceTheme';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;
const { Step } = Steps;

interface WorkboardProps {
  lead: Lead;
  onLeadChange: (lead: Lead) => void;
  /** Strip outer Card; use inside a parent shell (e.g. unified ficha). */
  embedded?: boolean;
  /** Admin-only: view workboard as another user (display only; list driven by parent). */
  adminViewAsUserId?: number | null;
  adminViewAsUsers?: Array<User & { display_name?: string }>;
  onAdminViewAsUserChange?: (userId: number) => void;
  loadingAdminUsers?: boolean;
}

type SectionKey = 'entrada' | 'feedback' | 'registro' | 'instalados';

const SECTION_ORDER: SectionKey[] = ['entrada', 'feedback', 'registro', 'instalados'];

const sectionLabels: Record<SectionKey, string> = {
  entrada: 'Entrada',
  feedback: 'Feedback',
  registro: 'Registo',
  instalados: 'Instalados',
};

const getBusinessAreaKey = (lead: Lead): 'telecommunications' | 'energy_solutions' => {
  const raw = Array.isArray(lead.business_area)
    ? (lead.business_area as unknown as string[]).join(',')
    : lead.business_area || '';
  if (raw.includes('telecommunications')) return 'telecommunications';
  if (raw.includes('energy_solutions')) return 'energy_solutions';
  return 'telecommunications';
};

const getStatusOptions = (
  area: 'telecommunications' | 'energy_solutions',
  section: SectionKey
): string[] => {
  const sections =
    area === 'telecommunications'
      ? (TELECOM_SECTIONS as Record<string, string[]>)
      : (ENERGY_SECTIONS as Record<string, string[]>);

  if (section === 'instalados') return ['Por Instalar', 'Instalados'];
  return sections[section] || [];
};

const inferSectionFromStatus = (
  status: string,
  area: 'telecommunications' | 'energy_solutions'
): SectionKey | null => {
  const sections =
    area === 'telecommunications'
      ? (TELECOM_SECTIONS as Record<string, string[]>)
      : (ENERGY_SECTIONS as Record<string, string[]>);

  for (const key of SECTION_ORDER) {
    if (sections[key]?.includes(status)) return key;
  }
  if (status === 'Por Instalar' || status === 'Instalados') return 'instalados';
  return null;
};

const Workboard: React.FC<WorkboardProps> = ({
  lead,
  onLeadChange,
  embedded = false,
  adminViewAsUserId,
  adminViewAsUsers,
  onAdminViewAsUserChange,
  loadingAdminUsers = false,
}) => {
  const { user } = useCognitoAuth();
  const area = useMemo(() => getBusinessAreaKey(lead), [lead]);
  const [section, setSection] = useState<SectionKey>(
    (lead.section as SectionKey) || 'entrada'
  );
  const [status, setStatus] = useState<string>(lead.lead_status || '');
  const [pendenteReason, setPendenteReason] = useState<string>(
    lead.pendente_reason || ''
  );
  const [followupNote, setFollowupNote] = useState<string>(lead.followup_note || '');
  const [seminteresseNote, setSeminteresseNote] = useState<string>(
    lead.seminteresse_note || ''
  );
  const [informacaoErradaNote, setInformacaoErradaNote] = useState<string>(
    lead.informacao_errada_note || ''
  );
  const [endOfContract, setEndOfContract] = useState<Moment | null>(
    lead.end_of_contract ? moment(lead.end_of_contract) : null
  );
  const [installationDate, setInstallationDate] = useState<Moment | null>(
    lead.installation_date ? moment(lead.installation_date) : null
  );
  const [nextFollowUp, setNextFollowUp] = useState<Moment | null>(
    lead.next_follow_up ? moment(lead.next_follow_up) : null
  );
  const [saving, setSaving] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  useEffect(() => {
    setSection((lead.section as SectionKey) || 'entrada');
    setStatus(lead.lead_status || '');
    setPendenteReason(lead.pendente_reason || '');
    setFollowupNote(lead.followup_note || '');
    setSeminteresseNote(lead.seminteresse_note || '');
    setInformacaoErradaNote(lead.informacao_errada_note || '');
    setEndOfContract(lead.end_of_contract ? moment(lead.end_of_contract) : null);
    setInstallationDate(lead.installation_date ? moment(lead.installation_date) : null);
    setNextFollowUp(lead.next_follow_up ? moment(lead.next_follow_up) : null);
  }, [lead]);

  const handleStatusChange = (next: string) => {
    setStatus(next);
    const inferred = inferSectionFromStatus(next, area);
    if (inferred) setSection(inferred);
  };

  const handleSectionChange = (next: SectionKey) => {
    setSection(next);
    const opts = getStatusOptions(area, next);
    if (!opts.includes(status)) {
      setStatus(opts[0] || '');
    }
  };

  const handleAdvance = () => {
    const idx = SECTION_ORDER.indexOf(section);
    if (idx >= 0 && idx < SECTION_ORDER.length - 1) {
      handleSectionChange(SECTION_ORDER[idx + 1]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Partial<Lead> = {
        section,
        lead_status: status,
        pendente_reason: status === 'Pendente' ? pendenteReason : null,
        followup_note: status === 'Follow-up' ? followupNote : lead.followup_note,
        seminteresse_note:
          status === 'Sem Interesse' ? seminteresseNote : lead.seminteresse_note,
        informacao_errada_note:
          status === 'Informação Errada' ? informacaoErradaNote : lead.informacao_errada_note,
        end_of_contract:
          status === 'Fidelizado' && endOfContract
            ? endOfContract.format('YYYY-MM-DD')
            : lead.end_of_contract,
        installation_date:
          section === 'instalados' && installationDate
            ? installationDate.format('YYYY-MM-DD')
            : lead.installation_date,
        next_follow_up: nextFollowUp ? nextFollowUp.format('YYYY-MM-DD') : lead.next_follow_up,
        last_contact_date: moment().format('YYYY-MM-DD'),
      };

      const updated = await apiService.updateLead(lead.id, payload);
      onLeadChange(updated as Lead);
      message.success('Workboard atualizado.');
    } catch (e) {
      message.error('Erro ao guardar alterações no workboard.');
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = getStatusOptions(area, section);
  const currentSectionIdx = Math.max(0, SECTION_ORDER.indexOf(section));

  const headerActions = (
    <Space>
      {currentSectionIdx < SECTION_ORDER.length - 1 && (
        <Button icon={<ArrowRightOutlined />} onClick={handleAdvance}>
          Avançar etapa
        </Button>
      )}
      <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
        Guardar
      </Button>
      {userMayUseClientManagementEmailButton(user) ? (
        <Tooltip title="Enviar Email">
          <Button icon={<MailOutlined />} onClick={() => setEmailModalOpen(true)}>
            Enviar
          </Button>
        </Tooltip>
      ) : null}
    </Space>
  );

  const formBody = (
    <>
      <Steps
        size="small"
        current={currentSectionIdx}
        onChange={(idx) => handleSectionChange(SECTION_ORDER[idx])}
        style={{ marginBottom: 24 }}
      >
        {SECTION_ORDER.map((key) => (
          <Step key={key} title={sectionLabels[key]} />
        ))}
      </Steps>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Text strong>Etapa</Text>
          <Select
            value={section}
            style={{ width: '100%', marginTop: 4 }}
            onChange={(v) => handleSectionChange(v as SectionKey)}
          >
            {SECTION_ORDER.map((s) => (
              <Option key={s} value={s}>
                {sectionLabels[s]}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={12}>
          <Text strong>Estado</Text>
          <Select
            value={status || undefined}
            placeholder="Selecione o estado"
            style={{ width: '100%', marginTop: 4 }}
            onChange={handleStatusChange}
          >
            {statusOptions.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Divider />

      <Row gutter={16}>
        {status === 'Pendente' && (
          <Col xs={24} md={12}>
            <Text strong>Motivo da pendência</Text>
            <TextArea
              rows={2}
              value={pendenteReason}
              onChange={(e) => setPendenteReason(e.target.value)}
              placeholder="Indique o motivo"
            />
          </Col>
        )}
        {status === 'Follow-up' && (
          <Col xs={24} md={12}>
            <Text strong>Nota de Follow-up</Text>
            <TextArea
              rows={2}
              value={followupNote}
              onChange={(e) => setFollowupNote(e.target.value)}
            />
          </Col>
        )}
        {status === 'Sem Interesse' && (
          <Col xs={24} md={12}>
            <Text strong>Nota Sem Interesse</Text>
            <TextArea
              rows={2}
              value={seminteresseNote}
              onChange={(e) => setSeminteresseNote(e.target.value)}
            />
          </Col>
        )}
        {status === 'Informação Errada' && (
          <Col xs={24} md={12}>
            <Text strong>Nota Informação Errada</Text>
            <TextArea
              rows={2}
              value={informacaoErradaNote}
              onChange={(e) => setInformacaoErradaNote(e.target.value)}
            />
          </Col>
        )}
        {status === 'Fidelizado' && (
          <Col xs={24} md={12}>
            <Text strong>Fim da Fidelização</Text>
            <DatePicker
              style={{ width: '100%' }}
              value={endOfContract}
              onChange={(d) => setEndOfContract(d as Moment | null)}
            />
          </Col>
        )}
        {section === 'instalados' && (
          <Col xs={24} md={12}>
            <Text strong>Data de Instalação</Text>
            <DatePicker
              style={{ width: '100%' }}
              value={installationDate}
              onChange={(d) => setInstallationDate(d as Moment | null)}
            />
          </Col>
        )}
        <Col xs={24} md={12}>
          <Text strong>Próximo follow-up</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={nextFollowUp}
            onChange={(d) => setNextFollowUp(d as Moment | null)}
          />
        </Col>
      </Row>
    </>
  );

  const emailModal = (
    <EmailModal
      visible={emailModalOpen}
      onClose={() => setEmailModalOpen(false)}
      lead={emailModalOpen ? lead : null}
    />
  );

  const adminViewSelect =
    adminViewAsUsers && adminViewAsUsers.length > 0 && onAdminViewAsUserChange ? (
      <Select
        size="small"
        style={{ minWidth: 160, maxWidth: 220 }}
        value={adminViewAsUserId ?? undefined}
        onChange={onAdminViewAsUserChange}
        loading={loadingAdminUsers}
        disabled={loadingAdminUsers}
        showSearch
        optionFilterProp="children"
        placeholder="Utilizador..."
      >
        {adminViewAsUsers.map((u) => (
          <Option key={u.id} value={u.id}>
            {u.display_name || u.username}
          </Option>
        ))}
      </Select>
    ) : null;

  if (embedded) {
    return (
      <>
        <div
          style={{
            ...workspaceInset,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            padding: '10px 16px',
          }}
        >
          <Space wrap size={8}>
            <Text strong>Workboard</Text>
            {adminViewSelect ? (
              <>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  de
                </Text>
                {adminViewSelect}
              </>
            ) : null}
          </Space>
          {headerActions}
        </div>
        <div style={{ ...workspaceCardBody, padding: '16px 20px 20px' }}>{formBody}</div>
        {emailModal}
      </>
    );
  }

  return (
    <>
      <Card
        title="Workboard"
        style={{ ...workspaceSurface, marginBottom: 16 }}
        headStyle={workspaceCardHead}
        bodyStyle={{ ...workspaceCardBody, padding: '16px 20px 20px' }}
        extra={headerActions}
      >
        {formBody}
      </Card>
      {emailModal}
    </>
  );
};

export default Workboard;
